import { type FC, useMemo } from 'react';

import { DropdownHeaderOverflowMenu } from '@/features/selectionsPanel/sectionDropdown/dropdownHeaderOverflowMenu';
import { UploadCard } from '@/features/selectionsPanel/selectionCard/cardVariants/uploadCard';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import {
  type IBoundaryFeature,
  type ICLUFeature,
  type IParcelFeature,
  type IUploadFeature,
} from '@/models/features';
import { numberWithCommas, round } from '@/utils/common';
import { pluralize } from '@/utils/pluralize';

import { BoundaryCard } from '../selectionCard/cardVariants/boundaryCard';
import { DefaultCard } from '../selectionCard/cardVariants/defaultCard';
import { ParcelCard } from '../selectionCard/cardVariants/parcelCard';
import { getAcresOfFeatures } from '../util/determineAcres';
import { PanelSectionDropdown } from './panelSectionDropdown';

export const BoundariesSectionDropdown: FC = () => {
  const { selectedParcels, selectedFsa, selectedBoundaries, selectedUploads } =
    useSelectionsPanelContext();

  const combinedFeatures = useMemo(
    () => [
      ...selectedParcels,
      ...selectedFsa,
      ...selectedBoundaries,
      ...selectedUploads,
    ],
    [selectedBoundaries, selectedFsa, selectedParcels, selectedUploads]
  );

  const totalSelectionAcreage = useMemo(() => {
    const totalAcres = getAcresOfFeatures(combinedFeatures);

    return round(totalAcres, 2);
  }, [combinedFeatures]);

  const parcelCards = useMemo(() => {
    return selectedParcels.map((feature: IParcelFeature, idx) => (
      <ParcelCard key={feature.id} feature={feature} idx={idx} />
    ));
  }, [selectedParcels]);

  const fsaCards = useMemo(() => {
    const startingIndex = selectedParcels.length;

    return selectedFsa.map((feature: ICLUFeature, idx) => (
      <DefaultCard
        key={feature.id}
        feature={feature}
        idx={idx + startingIndex}
      />
    ));
  }, [selectedFsa, selectedParcels]);

  const boundaryCards = useMemo(() => {
    const startingIndex = selectedParcels.length + selectedFsa.length;

    return selectedBoundaries.map((feature: IBoundaryFeature, idx) => (
      <BoundaryCard
        key={feature.id}
        feature={feature}
        idx={idx + startingIndex}
      />
    ));
  }, [selectedBoundaries, selectedFsa, selectedParcels]);

  const uploadCards = useMemo(() => {
    const startingIndex =
      selectedParcels.length + selectedFsa.length + selectedBoundaries.length;

    return selectedUploads.map((feature: IUploadFeature, idx) => (
      <UploadCard
        key={feature.id}
        feature={feature}
        idx={idx + startingIndex}
      />
    ));
  }, [
    selectedBoundaries.length,
    selectedFsa.length,
    selectedParcels.length,
    selectedUploads,
  ]);

  if (!combinedFeatures.length) return null;

  return (
    <PanelSectionDropdown
      additionalHeaderElements={
        <DropdownHeaderOverflowMenu
          buttonClassName="h-8 w-8"
          features={combinedFeatures}
        />
      }
      sectionCount={combinedFeatures.length}
      subtitle={`${numberWithCommas(totalSelectionAcreage, true)} ${pluralize(
        'Acre',
        totalSelectionAcreage,
        false
      )}`}
      title="Boundaries"
    >
      {parcelCards}
      {fsaCards}
      {boundaryCards}
      {uploadCards}
    </PanelSectionDropdown>
  );
};
